'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Transaction {
  id: string;
  timestamp: string;
  account: string;
  description: string;
  debit: number;
  credit: number;
  hash: string;
  status: 'verified' | 'pending' | 'flagged';
}

interface TransactionStreamProps {
  transactions: Transaction[];
}

const TransactionStream = ({ transactions }: TransactionStreamProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  const formatCurrency = (value: number): string => {
    if (value === 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatTimestamp = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const getStatusConfig = (status: Transaction['status']) => {
    switch (status) {
      case 'verified':
        return { icon: 'CheckCircleIcon', color: 'text-success', bg: 'bg-success/10' };
      case 'pending':
        return { icon: 'ClockIcon', color: 'text-warning', bg: 'bg-warning/10' };
      case 'flagged':
        return { icon: 'ExclamationTriangleIcon', color: 'text-error', bg: 'bg-error/10' };
    }
  };

  const displayedTransactions = isExpanded ? transactions : transactions.slice(0, 5);

  return (
    <div className="bg-card border border-border rounded-md shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-border bg-secondary flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="BoltIcon" size={20} className="text-accent animate-pulse-glow" />
          <h2 className="font-mono text-lg font-semibold text-foreground">Live Transaction Stream</h2>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary text-foreground border border-border rounded hover:bg-muted transition-all duration-250 active:scale-98"
        >
          <span className="font-mono text-xs font-medium">
            {isExpanded ? 'Collapse' : 'Expand All'}
          </span>
          <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={14} />
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary border-b border-border">
              <th className="px-6 py-3 text-left font-mono text-xs font-semibold text-foreground">
                Timestamp
              </th>
              <th className="px-6 py-3 text-left font-mono text-xs font-semibold text-foreground">
                Account
              </th>
              <th className="px-6 py-3 text-left font-mono text-xs font-semibold text-foreground">
                Description
              </th>
              <th className="px-6 py-3 text-right font-mono text-xs font-semibold text-foreground">
                Debit
              </th>
              <th className="px-6 py-3 text-right font-mono text-xs font-semibold text-foreground">
                Credit
              </th>
              <th className="px-6 py-3 text-center font-mono text-xs font-semibold text-foreground">
                Status
              </th>
              <th className="px-6 py-3 text-center font-mono text-xs font-semibold text-foreground">
                Hash
              </th>
            </tr>
          </thead>
          <tbody>
            {displayedTransactions.map((txn) => {
              const statusConfig = getStatusConfig(txn.status);
              return (
                <tr
                  key={txn.id}
                  className="border-b border-border hover:bg-secondary/50 transition-all duration-250 cursor-pointer"
                  onClick={() => setSelectedTransaction(txn)}
                >
                  <td className="px-6 py-3">
                    <span className="font-data text-xs text-foreground">{formatTimestamp(txn.timestamp)}</span>
                  </td>
                  <td className="px-6 py-3">
                    <span className="font-mono text-xs font-medium text-foreground">{txn.account}</span>
                  </td>
                  <td className="px-6 py-3">
                    <span className="font-sans text-xs text-text-secondary">{txn.description}</span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <span className="font-data text-xs text-foreground">{formatCurrency(txn.debit)}</span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <span className="font-data text-xs text-foreground">{formatCurrency(txn.credit)}</span>
                  </td>
                  <td className="px-6 py-3 text-center">
                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded ${statusConfig.bg}`}>
                      <Icon name={statusConfig.icon as any} size={12} className={statusConfig.color} />
                    </div>
                  </td>
                  <td className="px-6 py-3 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(txn.hash);
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-primary text-text-secondary border border-border rounded hover:bg-muted transition-all duration-250"
                      title={txn.hash}
                    >
                      <Icon name="FingerPrintIcon" size={12} />
                      <span className="font-data text-xs">{txn.hash.substring(0, 8)}...</span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!isExpanded && transactions.length > 5 && (
        <div className="px-6 py-3 border-t border-border bg-secondary text-center">
          <button
            onClick={() => setIsExpanded(true)}
            className="font-mono text-xs font-medium text-accent hover:text-accent/80 transition-colors duration-250"
          >
            View {transactions.length - 5} more transactions
          </button>
        </div>
      )}
      {selectedTransaction && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-modal flex items-center justify-center p-4"
          onClick={() => setSelectedTransaction(null)}
        >
          <div
            className="bg-card border border-border rounded-md shadow-xl max-w-2xl w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-lg font-semibold text-foreground">Transaction Details</h3>
              <button
                onClick={() => setSelectedTransaction(null)}
                className="flex items-center justify-center w-8 h-8 bg-secondary text-text-secondary border border-border rounded hover:bg-muted transition-all duration-250"
              >
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="font-sans text-sm text-text-secondary">Transaction ID</span>
                  <p className="font-data text-sm font-medium text-foreground mt-1">
                    {selectedTransaction.id}
                  </p>
                </div>
                <div>
                  <span className="font-sans text-sm text-text-secondary">Timestamp</span>
                  <p className="font-data text-sm font-medium text-foreground mt-1">
                    {formatTimestamp(selectedTransaction.timestamp)}
                  </p>
                </div>
                <div>
                  <span className="font-sans text-sm text-text-secondary">Account</span>
                  <p className="font-mono text-sm font-medium text-foreground mt-1">
                    {selectedTransaction.account}
                  </p>
                </div>
                <div>
                  <span className="font-sans text-sm text-text-secondary">Status</span>
                  <div className="mt-1">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded ${
                        getStatusConfig(selectedTransaction.status).bg
                      }`}
                    >
                      <Icon
                        name={getStatusConfig(selectedTransaction.status).icon as any}
                        size={14}
                        className={getStatusConfig(selectedTransaction.status).color}
                      />
                      <span
                        className={`font-mono text-xs font-medium ${
                          getStatusConfig(selectedTransaction.status).color
                        }`}
                      >
                        {selectedTransaction.status.toUpperCase()}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <span className="font-sans text-sm text-text-secondary">Description</span>
                <p className="font-sans text-sm text-foreground mt-1">{selectedTransaction.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="font-sans text-sm text-text-secondary">Debit</span>
                  <p className="font-data text-lg font-semibold text-foreground mt-1">
                    {formatCurrency(selectedTransaction.debit)}
                  </p>
                </div>
                <div>
                  <span className="font-sans text-sm text-text-secondary">Credit</span>
                  <p className="font-data text-lg font-semibold text-foreground mt-1">
                    {formatCurrency(selectedTransaction.credit)}
                  </p>
                </div>
              </div>
              <div>
                <span className="font-sans text-sm text-text-secondary">Hash Fingerprint</span>
                <div className="flex items-center gap-2 mt-1">
                  <p className="font-data text-xs text-foreground bg-primary px-3 py-2 rounded border border-border flex-1 break-all">
                    {selectedTransaction.hash}
                  </p>
                  <button
                    onClick={() => navigator.clipboard.writeText(selectedTransaction.hash)}
                    className="flex items-center justify-center w-10 h-10 bg-accent text-accent-foreground rounded hover:shadow-lg transition-all duration-250 active:scale-98"
                    title="Copy hash"
                  >
                    <Icon name="ClipboardDocumentIcon" size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionStream;